import { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import QuestionHtmlWithZoom from '../QuestionHtmlWithZoom';
import { watchImageHeight } from '../../utils/imageHeightLimit';
import { ImageItem } from '../../types/question';

interface Question {
  id: string;
  type: string;
  subType?: string;
  title?: string;
  content?: string;
  titleImages?: ImageItem[];
  titleImageWidths?: number[];
  titleImageLayout?: string;
  imagePosition?: string;
  [key: string]: any;
}

interface QuizQuestionTitleProps {
  question: Question;
  currentIndex: number;
  examQuestions: Question[];
  onExamQuestionsChange?: (newQuestions: Question[] | ((prev: Question[]) => Question[])) => void;
  onHasWidthChangesChange?: (hasChanges: boolean) => void;
  isPreview?: boolean;
  canScaleImages?: boolean;
  showCodingTitle?: boolean;
  sizeVariant?: 'default' | 'compact';
  numberColor?: string;
}

// 快捷缩放按钮值（与编辑模式一致）
const QUICK_SCALES = [20, 30, 40, 50, 60, 70, 80, 90, 99];

// 获取图片URL（兼容 /sb-api/ 前缀和完整URL）
const getImageUrl = (url: unknown): string => {
  if (typeof url !== 'string' || !url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/sb-api/')) {
    const supabaseUrl = (window as any).__SUPABASE_URL__ || '';
    return supabaseUrl + url.replace('/sb-api/', '/');
  }
  if (url.startsWith('/storage/')) {
    const supabaseUrl = (window as any).__SUPABASE_URL__ || '';
    return supabaseUrl + url;
  }
  return url;
};

interface ImageSizeState {
  scale: number;
  rect: DOMRect;
}

export default function QuizQuestionTitle({
  question,
  currentIndex,
  examQuestions,
  onExamQuestionsChange,
  onHasWidthChangesChange,
  isPreview = false,
  canScaleImages = false,
  showCodingTitle = false,
  sizeVariant = 'default',
  numberColor = 'text-blue-600',
}: QuizQuestionTitleProps) {
  // 独立题干图片容器 ref
  const titleImagesContainerRef = useRef<HTMLDivElement>(null);
  // 题干整体容器 ref（包含 HTML 内嵌图片和独立 titleImages）
  const containerRef = useRef<HTMLDivElement>(null);
  // 缩放面板 ref
  const sliderRef = useRef<HTMLDivElement>(null);

  // 缩放面板状态
  const [selectedImage, setSelectedImage] = useState<HTMLImageElement | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(-1);
  const [imageSize, setImageSize] = useState<ImageSizeState | null>(null);
  const [showSlider, setShowSlider] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // 是否启用点击缩放交互
  const enableScaling = isPreview && canScaleImages;

  // 图片高度限制
  useEffect(() => {
    const container = titleImagesContainerRef.current;
    if (!container) return;

    let cleanupFn: (() => void) | null = null;
    let cancelled = false;

    const rafId = requestAnimationFrame(() => {
      if (cancelled) return;
      try {
        cleanupFn = watchImageHeight(container);
      } catch (error) {
        console.warn('Failed to initialize title image height observer:', error);
      }
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      if (cleanupFn) cleanupFn();
    };
  }, [question?.titleImages, question?.titleImageWidths]);

  // 题干内容
  const questionStem = question?.content || '';
  const processedStem = questionStem;

  // 图片布局参数
  const imagePosition = (question as any)?.imagePosition || 'below';

  // 判断是否有题干图片
  const hasImagesInContent = /<img/i.test(questionStem);
  const hasTitleImages = question?.titleImages && question.titleImages.length > 0 && !questionStem?.includes('<img');
  const hasAnyTitleImages = hasImagesInContent || hasTitleImages;

  // 计算题干图片数量
  const titleImageCount = useMemo(() => {
    if (hasTitleImages && question?.titleImages) {
      return question.titleImages.length;
    }
    if (hasImagesInContent) {
      return (questionStem.match(/<img/gi) || []).length;
    }
    return 0;
  }, [hasTitleImages, hasImagesInContent, question?.titleImages, questionStem]);

  // 判断是否为编程题
  const isCodingQuestion = question?.type === 'python_coding' ||
                           question?.type === 'cpp_coding' ||
                           question?.type === 'scratch_coding';

  // 编程题题目名称
  const questionTitle = isCodingQuestion
    ? (question?.title ?? (() => {
        const codingQuestions = examQuestions.filter(q =>
          q.type === 'python_coding' || q.type === 'cpp_coding' || q.type === 'scratch_coding'
        );
        const codingIndex = codingQuestions.findIndex(q => q.id === question?.id) + 1;
        return `未命名${codingIndex}`;
      })())
    : null;

  // 单图独立缩放：仅更新被点击图片的宽度
  const handleScaleChange = useCallback((newScale: number) => {
    if (!selectedImage || selectedImageIndex < 0) return;
    // 1. 仅修改被点击图片的 DOM 样式（视觉反馈）
    selectedImage.style.width = `${newScale}%`;
    selectedImage.style.maxWidth = '100%';
    selectedImage.style.height = 'auto';
    // 2. 更新状态
    setImageSize(prev => prev ? { ...prev, scale: newScale } : null);
    setInputValue(String(newScale));
    // 3. 仅更新 titleImageWidths[selectedImageIndex]，其他元素保持不变
    // 关键修复：使用函数式更新，避免闭包陷阱导致覆盖其他修改（如选项图片）
    // 关键修复：同步更新 content 字段中的 <img style="width:N%">，否则 processQuestionHtmlForRender
    // 会优先从 HTML 读取旧宽度，导致切换题目后缩放数据"丢失"（实际 state 已更新但渲染被旧 HTML 覆盖）
    onExamQuestionsChange?.((prevQuestions: Question[]) => {
      const targetQ = prevQuestions.find(q => q.id === question.id);
      if (!targetQ) return prevQuestions;
      const currentWidths = [...(targetQ.titleImageWidths || Array(titleImageCount).fill(100))];
      while (currentWidths.length < titleImageCount) currentWidths.push(100);
      currentWidths[selectedImageIndex] = newScale;

      // 同步更新 content 字段中对应 <img> 的 style width
      let updatedContent = targetQ.content || '';
      if (updatedContent.includes('<img')) {
        let imgIdx = 0;
        updatedContent = updatedContent.replace(/<img([^>]*?)>/g, (match, attrs) => {
          const width = currentWidths[imgIdx] ?? 100;
          imgIdx++;
          if (/style="/i.test(attrs)) {
            return match.replace(/<img([^>]*?)>/, (_m, a: string) => {
              const updatedAttrs = a.replace(/style="([^"]*)"/i, (_sm: string, styleContent: string) => {
                const cleanedStyle = styleContent.replace(/(?<!max-)width:\s*\d+%;?\s*/g, '');
                return `style="width:${width}%;${cleanedStyle}"`;
              });
              return `<img${updatedAttrs}>`;
            });
          }
          return `<img${attrs} style="width:${width}%;">`;
        });
      }

      return prevQuestions.map(q =>
        q.id === question.id ? { ...q, titleImageWidths: currentWidths, content: updatedContent } : q
      );
    });
    onHasWidthChangesChange?.(true);
  }, [selectedImage, selectedImageIndex, question.id, titleImageCount, onExamQuestionsChange, onHasWidthChangesChange]);

  // 数字输入框值变化
  const handleInputChange = useCallback((val: string) => {
    setInputValue(val);
    const num = parseInt(val);
    if (!isNaN(num) && num >= 10 && num <= 100 && selectedImage) {
      selectedImage.style.width = `${num}%`;
      selectedImage.style.maxWidth = '100%';
      selectedImage.style.height = 'auto';
      setImageSize(prev => prev ? { ...prev, scale: num } : null);
    }
  }, [selectedImage]);

  // 输入框失焦确认
  const handleInputBlur = useCallback(() => {
    const num = parseInt(inputValue);
    if (isNaN(num) || num < 10 || num > 100) {
      const currentScale = imageSize?.scale || 100;
      setInputValue(String(currentScale));
      return;
    }
    handleScaleChange(num);
  }, [inputValue, imageSize, handleScaleChange]);

  // 点击图片事件处理
  useEffect(() => {
    if (!enableScaling || !containerRef.current) return;
    const container = containerRef.current;

    const handleImageClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.tagName !== 'IMG') return;
      e.preventDefault();
      e.stopPropagation();

      // 获取容器内所有 img，确定被点击图片的索引
      const allImages = Array.from(container.querySelectorAll('img'));
      const idx = allImages.indexOf(target as HTMLImageElement);
      if (idx < 0) return;

      // 先隐藏再显示，避免位置计算错误
      setShowSlider(false);
      setSelectedImage(null);

      setTimeout(() => {
        // 清除其他图片的 selected 类
        container.querySelectorAll('img').forEach(img => img.classList.remove('selected'));
        (target as HTMLImageElement).classList.add('selected');

        // 从 DOM style 或 titleImageWidths 获取当前宽度
        const styleWidth = parseInt((target as HTMLImageElement).style.width);
        const currentWidth = !isNaN(styleWidth) ? styleWidth
          : (question.titleImageWidths?.[idx] ?? 100);
        const rect = (target as HTMLImageElement).getBoundingClientRect();

        setSelectedImage(target as HTMLImageElement);
        setSelectedImageIndex(idx);
        setImageSize({ scale: currentWidth, rect });
        setInputValue(String(currentWidth));
        setShowSlider(true);
      }, 10);
    };

    container.addEventListener('click', handleImageClick, true);
    return () => { container.removeEventListener('click', handleImageClick, true); };
  }, [enableScaling, question.id, question.titleImageWidths]);

  // 点击外部隐藏缩放面板
  // 关键修复：使用捕获阶段（第三个参数 true）确保即使父元素 stopPropagation 也能收到事件
  // 同时增加 mousedown 监听，避免某些场景下 click 事件被吞掉
  useEffect(() => {
    if (!showSlider) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      // 点击在缩放面板内，不隐藏
      if (sliderRef.current && sliderRef.current.contains(target)) return;
      // 点击在容器内的图片上，不隐藏（由 handleImageClick 处理）
      if (containerRef.current && containerRef.current.contains(target)) {
        const el = target as HTMLElement;
        if (el.tagName === 'IMG') return;
      }
      // 其他情况隐藏
      setShowSlider(false);
      setSelectedImage(null);
      setSelectedImageIndex(-1);
      // 清除 selected 类
      containerRef.current?.querySelectorAll('img').forEach(img => img.classList.remove('selected'));
    };
    // 使用捕获阶段确保事件一定能被捕获
    document.addEventListener('click', handleClickOutside, true);
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [showSlider]);

  // 滚动/resize 时更新缩放面板位置
  useEffect(() => {
    if (!showSlider || !selectedImage) return;
    const updatePosition = () => {
      try {
        const rect = selectedImage.getBoundingClientRect();
        setImageSize(prev => prev ? { ...prev, rect } : null);
      } catch {
        // DOM 节点已被移除，静默忽略
      }
    };
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showSlider, selectedImage]);

  // 切换题目时隐藏面板
  useEffect(() => {
    setShowSlider(false);
    setSelectedImage(null);
    setSelectedImageIndex(-1);
    setImageSize(null);
  }, [question.id]);

  // 题干文本渲染
  const titleText = processedStem?.includes('<img') || processedStem?.startsWith('<') ? (
    <QuestionHtmlWithZoom
      html={processedStem}
      imageWidths={question?.titleImageWidths}
    />
  ) : (
    <span>{processedStem}</span>
  );

  // 独立题干图片渲染
  const titleImagesEl = hasTitleImages ? (
    <div ref={titleImagesContainerRef}>
      {question!.titleImages!.map((imgItem, idx) => {
        const imgUrl = typeof imgItem === 'string' ? imgItem : imgItem?.url;
        const fullUrl = getImageUrl(imgUrl);
        const savedWidth = (typeof imgItem === 'object' && imgItem?.widthPercent)
          ? imgItem.widthPercent
          : question?.titleImageWidths?.[idx];
        const displayWidth = savedWidth != null ? savedWidth : 100;

        return (
          <div key={idx} className="relative inline-block">
            <img
              src={fullUrl}
              alt={`题目图片 ${idx + 1}`}
              className="question-img rounded-lg border border-gray-200"
              style={{ width: `${displayWidth}%`, height: 'auto', objectFit: 'contain' }}
              loading="lazy"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          </div>
        );
      })}
    </div>
  ) : null;

  // 外层容器样式
  const containerClassName = sizeVariant === 'compact'
    ? 'text-sm font-medium text-gray-900 leading-[1.3]'
    : 'text-sm sm:text-base lg:text-lg font-medium text-gray-900 leading-[1.3]';

  return (
    <div className={containerClassName}>
      <div className="flex items-start gap-2">
        <span className={`${numberColor} font-bold flex-shrink-0`}>{currentIndex + 1}.</span>
        <div className="flex-1 min-w-0">
          {/* 编程题题目名称 */}
          {showCodingTitle && isCodingQuestion && questionTitle && (
            <div className="mb-3 pb-3 border-b border-gray-200">
              <span className="text-sm font-medium text-gray-500">题目名称：</span>
              <span className="text-base font-semibold text-gray-800">{questionTitle}</span>
            </div>
          )}

          {/* 题干内容 + 图片布局 */}
          <div ref={containerRef} className={`relative ${enableScaling ? 'cursor-pointer' : ''}`}>
            {imagePosition === 'above' && titleImagesEl}
            {titleText}
            {imagePosition === 'below' && titleImagesEl}
          </div>
        </div>
      </div>

      {/* 点击图片弹出的缩放面板（参照编辑模式 QuillEditorWithImageResize） */}
      {showSlider && imageSize && selectedImage && (
        <div
          ref={sliderRef}
          className="image-size-slider"
          style={{
            position: 'fixed',
            left: `${Math.max(10, Math.min(imageSize.rect.left + imageSize.rect.width / 2 - 120, window.innerWidth - 260))}px`,
            top: `${Math.max(10, imageSize.rect.bottom + 10)}px`,
            zIndex: 9999
          }}
        >
          <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg min-w-[240px]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">图片缩放</span>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min="10"
                  max="100"
                  value={inputValue}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onBlur={handleInputBlur}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleInputBlur(); } }}
                  className="w-12 h-5 text-xs text-center border border-gray-300 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                />
                <span className="text-xs text-gray-500">%</span>
              </div>
            </div>
            {/* 快捷缩放按钮 */}
            <div className="flex flex-wrap gap-1.5 mb-2">
              {QUICK_SCALES.map((preset) => (
                <button
                  key={preset}
                  onClick={() => handleScaleChange(preset)}
                  className={`px-2 py-0.5 text-xs font-medium rounded transition-colors ${
                    imageSize.scale === preset
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {preset}
                </button>
              ))}
            </div>
            <input
              type="range"
              min="10"
              max="100"
              value={imageSize.scale}
              onChange={(e) => handleScaleChange(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${imageSize.scale - 10}%, #e5e7eb ${imageSize.scale - 10}%, #e5e7eb 100%)`
              }}
            />
            <div className="flex justify-between mt-1 text-xs text-gray-400">
              <span>10</span>
              <span>100</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
